import { useState } from "react";
import { useDebounceCallback } from "usehooks-ts";
import SelectComponent from "@via/components/select/select.tsx";
import { SelectOption } from "@via/utils/interfaces.tsx";

type FilterItem = { id: string; value: unknown };

type FoundryModelsFilterProps = {
  columnFilters: FilterItem[];
  setColumnFilters: (
    filters: (prevFilters: FilterItem[]) => FilterItem[]
  ) => void;
  setPage: (page: number) => void;
  setPageSize: (pageSize: number) => void;
};

const FoundryModelsFilter = ({
  columnFilters,
  setColumnFilters,
  setPage,
  setPageSize,
}: FoundryModelsFilterProps) => {
  const [nameFilter, setNameFilter] = useState(
    (columnFilters.find(filter => filter.id === "name")?.value as string) || ""
  );

  const [statusFilter, setStatusFilter] = useState<string | null>(
    (columnFilters.find(filter => filter.id === "status")?.value as
      | string
      | null) || null
  );

  const [taskTypeFilter, setTaskTypeFilter] = useState<string | null>(
    (columnFilters.find(filter => filter.id === "taskType")?.value as
      | string
      | null) || null
  );

  const debounced = useDebounceCallback((id, value) => {
    setColumnFilters(prevFilters => {
      const updatedFilters = prevFilters.map(filter =>
        filter.id === id ? { ...filter, value } : filter
      );
      if (!prevFilters.find(filter => filter.id === id) && value !== "") {
        return [...updatedFilters, { id, value }];
      }
      return updatedFilters.filter(filter => filter.value !== "");
    });
    setPage(0);
    setPageSize(10);
  }, 300);

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setNameFilter(value);
    debounced("name", value);
  };

  const handleStatusChange = (option: SelectOption | null) => {
    const value = option ? (option.value as string) : null;
    setStatusFilter(value);
    setColumnFilters(prevFilters => {
      const updatedFilters = prevFilters.map(filter =>
        filter.id === "status" ? { ...filter, value } : filter
      );
      if (
        !prevFilters.find(filter => filter.id === "status") &&
        value !== null
      ) {
        return [...updatedFilters, { id: "status", value }];
      }
      return updatedFilters.filter(
        filter => filter.id !== "status" || filter.value !== null
      );
    });
    setPage(0);
    setPageSize(10);
  };

  const handleTaskTypeChange = (option: SelectOption | null) => {
    const value = option ? (option.value as string) : null;
    setTaskTypeFilter(value);
    setColumnFilters(prevFilters => {
      const updatedFilters = prevFilters.map(filter =>
        filter.id === "taskType" ? { ...filter, value } : filter
      );
      if (
        !prevFilters.find(filter => filter.id === "taskType") &&
        value !== null
      ) {
        return [...updatedFilters, { id: "taskType", value }];
      }
      return updatedFilters.filter(
        filter => filter.id !== "taskType" || filter.value !== null
      );
    });
    setPage(0);
    setPageSize(10);
  };

  const statusOptions = [
    { label: "Training", value: "Training" },
    { label: "Deployed", value: "Deployed" },
    { label: "Testing", value: "Testing" },
    { label: "Production", value: "Production" },
    { label: "Archived", value: "Archived" },
  ];

  const taskTypeOptions = [
    { label: "Classification", value: "Classification" },
    { label: "Regression", value: "Regression" },
    { label: "Embedding", value: "Embedding" },
    { label: "Sequence model", value: "Sequence model" },
    { label: "LLM / text generation", value: "LLM / text generation" },
    { label: "Image model", value: "Image model" },
    { label: "QC flag prediction", value: "QC flag prediction" },
  ];

  return (
    <div className="w-full my-2 flex flex-col items-baseline justify-between gap-2 md:flex-row">
      <div className="w-full flex-1">
        <input
          type="text"
          placeholder="Filter By Name"
          className="w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
          value={nameFilter}
          onChange={handleNameChange}
        />
      </div>
      <div className="w-full flex-1">
        <SelectComponent
          name="filter-status"
          placeholder="Filter By Status"
          options={statusOptions}
          onChange={e => handleStatusChange(e as SelectOption)}
          value={
            statusFilter !== null
              ? {
                  label:
                    statusOptions.find(opt => opt.value === statusFilter)
                      ?.label || "Unknown",
                  value: statusFilter,
                }
              : null
          }
          enableClear={true}
        />
      </div>
      <div className="w-full flex-1">
        <SelectComponent
          name="filter-task-type"
          placeholder="Filter By Task Type"
          options={taskTypeOptions}
          onChange={e => handleTaskTypeChange(e as SelectOption)}
          value={
            taskTypeFilter !== null
              ? {
                  label:
                    taskTypeOptions.find(opt => opt.value === taskTypeFilter)
                      ?.label || "Unknown",
                  value: taskTypeFilter,
                }
              : null
          }
          enableClear={true}
        />
      </div>
    </div>
  );
};

export default FoundryModelsFilter;
